import type { FeatureContext } from './featureContext';
import { initChat, type ChatCardInput } from '../chat';
import { LastSaid } from '../lastSaid';
import { canIgnoreName, shouldSuppressSpeech } from '../ignoreList';
import { openIgnoreMenu } from '../ignoreMenu';
import { hueColor, hueCss } from '../render/huePalette';
import {
  shouldJournalSpeech,
  COMBAT_HERALD_NAME,
  LEADERBOARD_HERALD_NAME,
} from '../net/protocol';
import { awardCue } from '../audio/awardCue';
import { bankCue } from '../audio/bankCue';
import { cardDef, type CardRarity } from '../sim/index';

/**
 * The fixed yellow an **action/emote** `Speech` renders in (Cosmetic Progression,
 * PRD #101) - the classic UO emote colour. A line flagged `action: true` (the
 * "New Cosmetic Award!" emote) draws in this colour regardless of the message's
 * `hue`, distinct from ordinary speech which uses the speaker's chosen hue. Given
 * as a packed RGB for the Pixi overhead and the matching CSS for the journal line.
 */
const ACTION_EMOTE_COLOR = 0xffff00;
const ACTION_EMOTE_CSS = '#ffff00';

/**
 * Duel combat red (Player duels, PRD #252, ADR-0131): the "OPPONENT is attacking
 * you!" overhead line (a `Speech` stamped from {@link COMBAT_HERALD_NAME}) draws in
 * this red regardless of hue, so it reads red even under the stub hue ramp.
 */
const COMBAT_RED_COLOR = 0xdd2222;
const COMBAT_RED_CSS = '#dd2222';

/** Local-storage key the player's chosen speech-text hue persists under across sessions. */
const SPEECH_HUE_KEY = 'britbank.speechHue';

/**
 * Read the player's saved speech-text hue (the colour their own lines render in),
 * or `0` (the plain-white hue-0 default) when none is stored or storage is
 * unavailable. Persisted only in the browser - the server keeps it per-session.
 */
function loadSpeechHue(): number {
  try {
    const raw = localStorage.getItem(SPEECH_HUE_KEY);
    const hue = raw === null ? 0 : Number.parseInt(raw, 10);
    return Number.isInteger(hue) && hue > 0 ? hue : 0;
  } catch {
    return 0;
  }
}

/** Persist the chosen speech-text hue so it survives a reload/reconnect. */
function saveSpeechHue(hue: number): void {
  try {
    localStorage.setItem(SPEECH_HUE_KEY, String(hue));
  } catch {
    /* storage unavailable (private mode / disabled); the choice is session-only. */
  }
}

/**
 * Wrap an action/emote line in the classic UO `*asterisks*` so it reads as an
 * action (`*New Cosmetic Award!*`) rather than a spoken line; ordinary speech is
 * returned unchanged.
 */
function formatSpeechText(text: string, action: boolean): string {
  return action ? `*${text}*` : text;
}

/** Injectable seams for unit tests; defaults are the real modules. */
export interface ChatInstallDeps {
  initChat?: typeof initChat;
}

/**
 * The chat overlay: send typed lines up as `Say` (through whichever client is
 * currently connected) - after the shared chat-command interceptors (the `/dress`
 * `/duel` `/ignore` family, each contributed by its own installer) - and surface
 * inbound `Speech` on its two surfaces: fading overhead text in the speaker's hue
 * (every line) and a persistent journal line (player speech only; ambient NPC barks
 * are kept out - ADR-0075). The speaker's identity is stamped by the server, so
 * every client renders the same attribution. The speech-bubble settings button lets
 * the player pick their overhead text colour, persisted and forwarded per connection.
 * Also owns the "say last" hotkey (Ctrl+Q) and the per-viewer squelch filtering
 * (the shared ignore list) on both speech surfaces.
 */
export function installChat(ctx: FeatureContext, deps: ChatInstallDeps = {}): void {
  // Tracks the last line the player actually sent, for the "say last" hotkey -
  // recorded on the say path below, recalled into the input by the action handler.
  const lastSaid = new LastSaid();

  // The player's chosen speech-text hue, restored from local storage. 0 is the
  // plain-white hue-0 default. It is told to the server per connection (on Welcome
  // and whenever the player picks a swatch), which stamps it onto broadcast lines.
  let speechHue = loadSpeechHue();

  // Offer the single-action "Ignore <name>" popover at the cursor, guarded so the
  // player can never ignore themselves (by their own typed name). The journal
  // line's right-click add-surface; committing adds the name and the store's
  // change subscription re-renders the Settings list and re-runs the live filter.
  const offerIgnore = (name: string, x: number, y: number): void => {
    if (!canIgnoreName(name, ctx.viewerName())) return;
    openIgnoreMenu({ name: name.trim(), x, y, onIgnore: (n) => ctx.state.ignoreList.add(n) });
  };

  const chat = (deps.initChat ?? initChat)(
    (text, cards) => {
      // Client-side commands (each contributed by its feature's installer: the
      // wardrobe's `/dress`, the movement core's `[moonwalk`, blood's `[bloodtest`,
      // duel's `/duel`, ignore's `/ignore`) intercept before the say path; a
      // consumed line never broadcasts and never counts as "last said". The spoken
      // "I wish to duel" arms without consuming, so it still broadcasts below.
      if (ctx.chatCommands.handle(text)) return;
      ctx.client()?.say(text, cards);
      // Record it as the last *outgoing* line so Ctrl+Q can recall it. Reached only
      // after the interceptors, so a client-only command never counts as "sent";
      // a blank line is ignored inside `record`.
      lastSaid.record(text);
    },
    {
      initialHue: speechHue,
      onPickHue: (hue) => {
        speechHue = hue;
        saveSpeechHue(hue);
        ctx.client()?.setSpeechHue(hue);
      },
      // Right-clicking a journal line offers "Ignore <name>" for its speaker.
      onIgnoreSpeaker: offerIgnore,
      // Consulted before the journal swallows the right-click: your own lines and the
      // system heralds stay on the native menu (right-click-copy) instead of a dead
      // popover. offerIgnore guards the same veto for defense in depth.
      canIgnoreSpeaker: (name) => canIgnoreName(name, ctx.viewerName()),
      onViewCard: (card) => ctx.events.emit('cardView', card),
    },
  );

  ctx.events.on('cardShare', (card) => chat.insertCard(card));
  ctx.events.on('chatHistoryRemove', (message) => chat.removeByAuthor(message.authorKey));

  // The movement ticker halts the character while the chat input has focus.
  ctx.state.provideChatFocus(() => chat.isInputFocused());

  // Say-last (Ctrl+Q): recall the player's last sent line into the chat input and
  // focus it, so a single Enter resends (or they edit first). It never auto-sends,
  // and it no-ops when nothing has been sent.
  ctx.actions.register('say-last', {
    down: () => {
      const last = lastSaid.recall();
      if (last !== null) chat.recallInput(last);
    },
  });

  // Restore the player's chosen speech colour on each fresh session (the server
  // keeps it per-connection, so a reconnect starts back at the white default).
  ctx.events.on('welcome', () => {
    if (speechHue > 0) ctx.client()?.setSpeechHue(speechHue);
  });

  ctx.events.on('speech', (speech) => {
    // An action/emote (the award emote, PRD #101) renders in the yellow `*…*` emote
    // style; ordinary overhead speech uses the speaker's chosen hue. The reference
    // journal uses fixed gold-name and muted-body tokens (ADR-0278).
    const action = speech.action === true;
    // The duel "OPPONENT is attacking you!" line (ADR-0131) is stamped from the
    // combat herald and drawn in combat red regardless of hue - the same "recognise
    // the system stamp" trick the yellow action emote uses. It floats overhead only
    // (server-stamped `npc`, so `shouldJournalSpeech` keeps it out of the journal),
    // and the server sends it to *only* the victim's own socket, so the alert is private.
    const combatLine = speech.name === COMBAT_HERALD_NAME;
    // Per-viewer ignore / squelch (client-side only): if the speaker is on this
    // viewer's ignore list, drop the line from BOTH surfaces (overhead bubble and
    // journal) and skip the cues below - the server keeps delivering, we just hide
    // it locally. Never suppresses your own lines, a SYSTEM action/emote, or a system
    // herald (see shouldSuppressSpeech), so /top, the award emote, and the combat
    // alert always show - but a player-authored action line (the `*dances*` chat
    // emote, stamped `playerAction`) IS ignorable, like the plain chat it wears the
    // emote styling over. The ignoring is by the server-stamped name.
    if (shouldSuppressSpeech(speech, ctx.playerId(), (n) => ctx.state.ignoreList.isIgnored(n))) {
      return;
    }
    const huePalette = ctx.assets.huePalette;
    const text = formatSpeechText(speech.text, action);
    const color = combatLine
      ? COMBAT_RED_COLOR
      : action
        ? ACTION_EMOTE_COLOR
        : hueColor(huePalette, speech.hue);
    const css = combatLine
      ? COMBAT_RED_CSS
      : action
        ? ACTION_EMOTE_CSS
        : hueCss(huePalette, speech.hue);
    // Overhead speech ages out on the rAF-backed Pixi ticker, which the browser
    // pauses while the tab is hidden - but the socket keeps delivering barks. Gating
    // the transient overhead surface on visibility means nothing accumulates while
    // hidden, so there is no backlog to flood the screen on refocus (issue #182; the
    // bootstrap's visibilitychange handler clears any that slipped through). The
    // journal append is intentionally *not* gated: real player speech that arrived
    // while hidden still lands in the durable log.
    //
    // A `journalOnly` line (unified Arena/bank chat) skips the overhead entirely:
    // it is a chat line from a *different* world (the bank talking to an Arena
    // runner, or vice versa), whose `entityId` names no valid sprite here - or,
    // worse, collides with an unrelated Arena enemy id (ADR-0176) - so it must
    // never float. It still journals and cues below like any other player line.
    if (!ctx.ui.hidden && !speech.journalOnly) {
      ctx.renderer()?.showSpeech(speech.entityId, text, color);
    }
    // Every line floats overhead, but only real player speech is journalled. The
    // server stamps ambient roster barks with `npc: true` so they stay overhead-only
    // (ADR-0075); the private, self-only award emote is likewise floated + cued but
    // kept out of the log (ADR-0126). Other player-entity lines - ordinary chat, the
    // `/top` herald, the dice-roll emote - still journal. `shouldJournalSpeech` is the
    // single durable-journal rule, keyed off the shared `isAwardEmote` predicate the
    // overhead cue below also uses, so the two surfaces can't drift.
    if (shouldJournalSpeech(speech)) {
      const kind = speech.name === LEADERBOARD_HERALD_NAME ? 'herald' : action ? 'action' : 'speech';
      // Resolve each attached card's display name so the journal can turn its
      // `[Card Name]` token into a link. A stale or malformed reference is skipped
      // (it stays readable as ordinary text) without discarding the others, so one
      // bad card can never break the sibling links in the same line.
      const cards: ChatCardInput[] = [];
      if (!action && speech.cards) {
        for (const ref of speech.cards) {
          try {
            const def = cardDef(ref.cardId, ref.rarity as CardRarity);
            cards.push({ ...ref, name: def.name });
          } catch {
            // A stale or malformed card reference stays readable as ordinary text.
          }
        }
      }
      chat.append(speech.name, text, css, {
        action,
        kind,
        ...(speech.authorKey ? { authorKey: speech.authorKey } : {}),
        ...(cards.length > 0 ? { cards } : {}),
      });
    }
    // The award emote is the server's private, self-only "New Cosmetic Award!"
    // action line, sent to the earner alone and only on a genuine new unlock - so it
    // is the one source of truth for "I just levelled up". Sound the level-up cue
    // here, in sync with the yellow emote; `awardCue` returns null for any other
    // speech. Playback routes through the same `AudioManager` the master mute governs.
    const cue = awardCue(speech);
    if (cue) ctx.audio.play(cue);
    // Easter egg (ADR-0078): any real player line that says "bank" (case-insensitive
    // substring) sounds the UO Kryss hit cue for everyone who heard the line. NPC
    // barks are excluded inside `bankCue`; the master mute governs it too.
    const bank = bankCue(speech);
    if (bank) ctx.audio.play(bank);
  });
}
